/**
 * MKS-469 — admin editor for `linear_team_project_map`, the setting that says
 * which Monksheets project a Linear team's issues are mirrored into.
 *
 * A team routes to its DEFAULT project. A label override sends an issue that
 * carries exactly that one Linear label to another project (the Fluidra phase
 * projects); several override labels fall back to the default, and the sync
 * reports it. Admins used to edit this only through a raw settings PATCH.
 *
 * The whole map is replaced on save (the PATCH has replace semantics), so the
 * page re-reads the stored map first and refuses to save when it changed since
 * this page loaded — another admin's teams are never silently overwritten. A
 * team without label rows is saved as a plain number, exactly as before.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from '../../components/common/Spinner';
import { useAppSettings, useUpdateAppSettings } from '../../hooks/useAdmin';
import { useProjects } from '../../hooks/useProjects';
import { getAppSettings, type LinearTeamProjectMapValue } from '../../services/api/admin';
import { toErrorMessage } from '../../utils/toErrorMessage';

type TeamMap = Record<string, LinearTeamProjectMapValue>;

// Keep in step with LINEAR_TEAM_KEY_PATTERN and the limits in
// backend/src/services/linearTeamRouting.ts (the server is the real gate).
const TEAM_KEY_PATTERN = /^[A-Z][A-Z0-9]{0,15}$/;
const MAX_TEAMS = 200;
const MAX_LABEL_ROUTES = 100;
const MAX_LABEL_NAME = 255;

interface LabelRow {
  rid: number;
  name: string;
  projectId: number | null;
}

interface TeamRow {
  rid: number;
  key: string;
  projectId: number | null;
  labels: LabelRow[];
}

const normalizeLabel = (name: string) => name.trim().toLowerCase();

/** A map in one canonical form, so two spellings of the same routing compare equal. */
function canonical(map: TeamMap): string {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(map).sort()) {
    const value = map[key];
    if (value === undefined) continue;
    const upper = key.trim().toUpperCase();
    if (typeof value === 'number') {
      out[upper] = value;
      continue;
    }
    const labels = value.labels ?? {};
    const names = Object.keys(labels).sort();
    out[upper] =
      names.length === 0
        ? value.project
        : { project: value.project, labels: Object.fromEntries(names.map((n) => [n.trim(), labels[n]])) };
  }
  return JSON.stringify(out);
}

function mapToRows(map: TeamMap | undefined, nextId: () => number): TeamRow[] {
  return Object.keys(map ?? {})
    .sort()
    .map((key) => {
      const value = (map as TeamMap)[key];
      if (value === undefined || typeof value === 'number') {
        return { rid: nextId(), key: key.toUpperCase(), projectId: value ?? null, labels: [] };
      }
      return {
        rid: nextId(),
        key: key.toUpperCase(),
        projectId: value.project,
        labels: Object.entries(value.labels ?? {}).map(([name, projectId]) => ({
          rid: nextId(),
          name,
          projectId,
        })),
      };
    });
}

/** The map the rows stand for. Call only on rows that pass `validate`. */
function rowsToMap(rows: TeamRow[]): TeamMap {
  const map: TeamMap = {};
  for (const row of rows) {
    const key = row.key.trim().toUpperCase();
    const project = row.projectId as number;
    if (row.labels.length === 0) {
      map[key] = project;
    } else {
      map[key] = {
        project,
        labels: Object.fromEntries(row.labels.map((l) => [l.name.trim(), l.projectId as number])),
      };
    }
  }
  return map;
}

interface RowErrors {
  team: Record<number, string>;
  label: Record<number, string>;
}

function validate(rows: TeamRow[]): RowErrors {
  const errors: RowErrors = { team: {}, label: {} };
  const seenTeams = new Set<string>();
  for (const row of rows) {
    const key = row.key.trim().toUpperCase();
    if (!TEAM_KEY_PATTERN.test(key)) {
      errors.team[row.rid] = 'Team key: a letter, then up to 15 letters or digits.';
    } else if (seenTeams.has(key)) {
      errors.team[row.rid] = `Team ${key} appears more than once.`;
    } else if (row.projectId === null) {
      errors.team[row.rid] = 'Choose a default project.';
    }
    seenTeams.add(key);

    const seenLabels = new Set<string>();
    for (const label of row.labels) {
      const name = normalizeLabel(label.name);
      if (name === '') {
        errors.label[label.rid] = 'Enter the Linear label name.';
      } else if (label.name.trim().length > MAX_LABEL_NAME) {
        errors.label[label.rid] = `Label names are at most ${MAX_LABEL_NAME} characters.`;
      } else if (seenLabels.has(name)) {
        errors.label[label.rid] = 'This label is already listed for the team (case is ignored).';
      } else if (label.projectId === null) {
        errors.label[label.rid] = 'Choose a project for this label.';
      }
      if (name !== '') seenLabels.add(name);
    }
  }
  return errors;
}

export default function LinearTeamRoutingPage() {
  const settings = useAppSettings();
  const projectsQuery = useProjects();
  const update = useUpdateAppSettings();

  const counter = useRef(0);
  const nextId = () => ++counter.current;

  const [rows, setRows] = useState<TeamRow[]>([]);
  const [snapshot, setSnapshot] = useState<TeamMap>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stored = settings.data?.linear_team_project_map;

  // Initialise from the stored map, and again on fresh data — but only while
  // the editor holds no unsaved edits. `dirty` is read through a ref so that
  // finishing a save (dirty -> false) does not re-seed the rows from the
  // not-yet-refetched, pre-save copy of the map.
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (!settings.data || dirtyRef.current) return;
    setRows(mapToRows(stored, nextId));
    setSnapshot(stored ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  const projects = useMemo(
    () => [...(projectsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [projectsQuery.data]
  );
  const projectIds = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);

  const errors = useMemo(() => validate(rows), [rows]);
  const invalid = Object.keys(errors.team).length > 0 || Object.keys(errors.label).length > 0;

  const edit = (fn: (draft: TeamRow[]) => TeamRow[]) => {
    setRows((current) => fn(current));
    setDirty(true);
    setSaved(false);
  };
  const updateTeam = (rid: number, patch: Partial<TeamRow>) =>
    edit((draft) => draft.map((t) => (t.rid === rid ? { ...t, ...patch } : t)));
  const updateLabel = (teamRid: number, labelRid: number, patch: Partial<LabelRow>) =>
    edit((draft) =>
      draft.map((t) =>
        t.rid === teamRid
          ? { ...t, labels: t.labels.map((l) => (l.rid === labelRid ? { ...l, ...patch } : l)) }
          : t
      )
    );

  const reset = () => {
    setDirty(false);
    setSaved(false);
    setError(null);
    setRows(mapToRows(snapshot, nextId));
  };

  const reload = async () => {
    setError(null);
    setSaved(false);
    setDirty(false);
    const fresh = (await settings.refetch()).data?.linear_team_project_map;
    setRows(mapToRows(fresh, nextId));
    setSnapshot(fresh ?? {});
  };

  const save = async () => {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      // The PATCH replaces the whole map, so a map changed since this page
      // loaded must not be overwritten with a stale copy of it.
      const fresh = await getAppSettings();
      if (canonical(fresh.linear_team_project_map ?? {}) !== canonical(snapshot)) {
        setError(
          'The routing map was changed elsewhere since this page loaded. Reload it, then apply your changes again.'
        );
        return;
      }
      const next = rowsToMap(rows);
      await update.mutateAsync({ linear_team_project_map: next });
      setSnapshot(next);
      setDirty(false);
      setSaved(true);
    } catch (err) {
      setError(toErrorMessage(err, 'Could not save the routing map.'));
    } finally {
      setSaving(false);
    }
  };

  if (settings.isLoading || projectsQuery.isLoading) {
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );
  }
  if (settings.isError) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-800">
          {toErrorMessage(settings.error, 'Could not load the routing map.')}
        </p>
      </div>
    );
  }

  const projectOptions = (selected: number | null) => (
    <>
      <option value="">Select a project…</option>
      {selected !== null && !projectIds.has(selected) && (
        <option value={selected}>{`#${selected} (not in the project list)`}</option>
      )}
      {projects.map((p) => (
        <option key={p.id} value={p.id}>
          {`${p.slug} — ${p.fullName || p.name}`}
        </option>
      ))}
    </>
  );
  const parseProject = (value: string): number | null => (value === '' ? null : Number(value));

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Linear team routing</h1>
        <p className="mt-1 text-sm text-gray-600">
          A Linear team&apos;s issues are mirrored into its default project. A label override
          sends an issue carrying exactly that one label to another project instead (for example
          a phase subproject). An issue with several override labels goes to the default project,
          and the sync reports it. Time entries never move with a task.
        </p>
      </div>

      {rows.length === 0 && (
        <p className="text-sm text-gray-500">No teams are mapped yet.</p>
      )}

      <div className="space-y-3">
        {rows.map((team) => {
          const teamLabel = team.key.trim() || 'new team';
          return (
            <div
              key={team.rid}
              className="bg-white rounded-lg border border-gray-200 p-4 space-y-3"
            >
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  aria-label="Team key"
                  value={team.key}
                  maxLength={16}
                  onChange={(e) => updateTeam(team.rid, { key: e.target.value.toUpperCase() })}
                  placeholder="TEAM"
                  className="w-28 rounded border border-gray-300 px-2 py-1 text-sm font-mono"
                />
                <select
                  aria-label={`Default project for ${teamLabel}`}
                  value={team.projectId ?? ''}
                  onChange={(e) => updateTeam(team.rid, { projectId: parseProject(e.target.value) })}
                  className="min-w-64 flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                >
                  {projectOptions(team.projectId)}
                </select>
                <button
                  type="button"
                  onClick={() => edit((draft) => draft.filter((t) => t.rid !== team.rid))}
                  className="text-sm text-red-700 underline"
                  aria-label={`Remove team ${teamLabel}`}
                >
                  Remove team
                </button>
              </div>
              {errors.team[team.rid] && (
                <p className="text-xs text-red-700">{errors.team[team.rid]}</p>
              )}

              {team.labels.length > 0 && (
                <p className="text-xs font-medium uppercase text-gray-500">Label overrides</p>
              )}
              {team.labels.map((label) => (
                <div key={label.rid} className="space-y-1 pl-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      type="text"
                      aria-label="Label name"
                      value={label.name}
                      onChange={(e) => updateLabel(team.rid, label.rid, { name: e.target.value })}
                      placeholder="Linear label"
                      className="w-56 rounded border border-gray-300 px-2 py-1 text-sm"
                    />
                    <select
                      aria-label={`Project for label ${label.name.trim() || 'new label'}`}
                      value={label.projectId ?? ''}
                      onChange={(e) =>
                        updateLabel(team.rid, label.rid, { projectId: parseProject(e.target.value) })
                      }
                      className="min-w-64 flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                    >
                      {projectOptions(label.projectId)}
                    </select>
                    <button
                      type="button"
                      onClick={() =>
                        updateTeam(team.rid, {
                          labels: team.labels.filter((l) => l.rid !== label.rid),
                        })
                      }
                      className="text-sm text-red-700 underline"
                      aria-label={`Remove label ${label.name.trim() || 'row'}`}
                    >
                      Remove label
                    </button>
                  </div>
                  {errors.label[label.rid] && (
                    <p className="text-xs text-red-700">{errors.label[label.rid]}</p>
                  )}
                </div>
              ))}

              <button
                type="button"
                disabled={team.labels.length >= MAX_LABEL_ROUTES}
                onClick={() =>
                  updateTeam(team.rid, {
                    labels: [...team.labels, { rid: nextId(), name: '', projectId: null }],
                  })
                }
                className="text-sm text-blue-700 underline disabled:opacity-50"
                aria-label={`Add label override for ${teamLabel}`}
              >
                Add label override
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={rows.length >= MAX_TEAMS}
          onClick={() =>
            edit((draft) => [...draft, { rid: nextId(), key: '', projectId: null, labels: [] }])
          }
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-800 disabled:opacity-50"
        >
          Add team
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={!dirty}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-800 disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty || invalid}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="text-sm text-green-700">Saved</span>}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2" role="alert">
          <p className="text-sm text-red-800">{error}</p>
          <button type="button" onClick={reload} className="text-sm text-red-800 underline">
            Reload from server
          </button>
        </div>
      )}
    </div>
  );
}
